import { useEffect, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import FormField from "../common/FormField";
import { formatDate } from "../../utils/format";
import { APPROVAL_STATUS } from "../../services/progressApprovalApi";
import { linkProblem } from "../../services/evidenceLinkApi";
import DraftNotice from "../common/DraftNotice";
import useFormDraft from "../../hooks/useFormDraft";

const latestSubmitted = (history) => history.find((h) => ["PENDING", "APPROVED", "NOT_REQUIRED"].includes(h.approvalStatus));

export default function GoalProgressModal({ open, goal, onClose, onSave, loadHistory }) {
  const [form, setForm] = useState({ achievement: "", employeeComments: "", evidence: null, evidenceFile: null, evidenceUrl: "", evidenceUrlTitle: "", markCompleted: false });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingBase, setPendingBase] = useState(null);
  const [historyReady, setHistoryReady] = useState(false); // [NEW] draft is restored only after history set the starting %

  useEffect(() => {
    if (open && goal) {
      setForm({ achievement: goal.achievement ?? "", employeeComments: "", evidence: null, evidenceFile: null, evidenceUrl: "", evidenceUrlTitle: "", markCompleted: goal.status === "Completed" || Number(goal.achievement) >= 100 });
      setError("");
      setHistory([]);
      setPendingBase(null);
      setHistoryReady(!loadHistory);
      if (loadHistory) loadHistory(goal.id).then((res) => {
        const rows = res.data || [];
        setHistory(rows);
        const latest = latestSubmitted(rows);
        if (latest?.approvalStatus === "PENDING" && latest.value !== null && latest.value !== undefined) {
          setPendingBase(latest);
          setForm((f) => (f.touched ? f : { ...f, achievement: Number(latest.value), markCompleted: latest.completed || Number(latest.value) >= 100 }));
        }
      }).catch(() => setHistory([])).finally(() => setHistoryReady(true));
    }
  }, [open, goal, loadHistory]);

  // [NEW] Keep unsaved progress text/% (the attached file itself cannot be kept; it must be chosen again).
  const draft = useFormDraft(`progress:${goal?.id}`, form, setForm, { active: Boolean(open && goal && historyReady), omit: ["evidence", "evidenceFile"] });

  const setAchievement = (value) => setForm((f) => ({ ...f, touched: true, achievement: value, markCompleted: value !== "" && Number(value) >= 100 }));
  const setCompleted = (checked) => setForm((f) => ({
    ...f,
    touched: true,
    markCompleted: checked,
    achievement: checked ? 100 : (Number(f.achievement) >= 100 ? 90 : f.achievement),
  }));

  const submit = async () => {
    const v = form.markCompleted ? 100 : form.achievement === "" ? null : Number(form.achievement);
    if (v !== null && (Number.isNaN(v) || v < 0 || v > 100)) {
      setError("Enter a number between 0 and 100.");
      return;
    }
    if (v === null && !String(form.employeeComments || "").trim()) {
      setError("Enter the % achieved, or write what moved since the last update.");
      return;
    }
    const url = String(form.evidenceUrl || "").trim();
    if (url) {
      const problem = linkProblem(url);
      if (problem) { setError(`Evidence link: ${problem}`); return; }
    }
    setSaving(true);
    const saved = await onSave({ ...form, evidenceUrl: url, evidenceUrlTitle: String(form.evidenceUrlTitle || "").trim() || goal.title, achievement: v });
    setSaving(false);
    if (saved) { draft.clear(); onClose(); }
  };

  if (!goal) return null;
  return (
    <Modal
      open={open}
      title="Update progress"
      subtitle={goal.title}
      onClose={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} loading={saving}>Save progress</Button>
        </>
      }
    >
      <div className="form-stack">
        <DraftNotice draft={draft} compact />
        <FormField
          label="Achievement %"
          error={error}
          hint={pendingBase
            ? `Target: ${goal.target} ${goal.unit}. Starts from your update waiting for approval (${pendingBase.value}%); the goal shows ${goal.achievement ?? 0}% until it is approved.`
            : `Target: ${goal.target} ${goal.unit}`}
        >
          <div className="range-row">
            <input type="range" min="0" max="100" value={form.achievement === "" ? 0 : form.achievement} onChange={(e) => setAchievement(e.target.value)} className="range" aria-label="Achievement slider" />
            <input type="number" min="0" max="100" value={form.achievement} onChange={(e) => setAchievement(e.target.value)} className="form-control range-number" />
          </div>
        </FormField>
        <FormField label="Progress update">
          <textarea rows={3} value={form.employeeComments} onChange={(e) => setForm((f) => ({ ...f, employeeComments: e.target.value }))} placeholder="What moved since the last update?" />
        </FormField>
        <FormField label="Evidence" hint={form.evidence ? `Attached: ${form.evidence}` : "Optional. PDF, PNG, JPEG or text."}>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.txt" className="form-control file-control" onChange={(e) => { const file = e.target.files?.[0]; setForm((f) => ({ ...f, evidence: file?.name || f.evidence, evidenceFile: file || f.evidenceFile })); }} />
        </FormField>
        <FormField label="Evidence link" hint="Optional. A https:// link to a document, dashboard, ticket or demo that shows this progress. Searchable later under Evidence.">
          <div className="form-stack" style={{ gap: 8 }}>
            <input className="form-control" type="url" placeholder="https://..." value={form.evidenceUrl} onChange={(e) => { setForm((f) => ({ ...f, evidenceUrl: e.target.value })); setError(""); }} />
            {String(form.evidenceUrl || "").trim() && (
              <input className="form-control" placeholder={`Link title (default: ${goal.title})`} maxLength={200} value={form.evidenceUrlTitle} onChange={(e) => setForm((f) => ({ ...f, evidenceUrlTitle: e.target.value }))} />
            )}
          </div>
        </FormField>
        <label className="checkbox">
          <input type="checkbox" checked={form.markCompleted} onChange={(e) => setCompleted(e.target.checked)} />
          <span>Mark this goal as completed</span>
        </label>
        <p className="form-hint" style={{ marginTop: -6 }}>
          A completed goal is saved at 100% and shows as <strong>Completed</strong>. If your cycle needs manager approval for
          progress, the goal changes only once your manager approves the update. This is a progress update only:
          your self assessment still has to be submitted at year end (Self assessment → Submit to manager).
        </p>
        {history.length > 0 && (
          <div>
            <p className="detail-label">Previous updates</p>
            <ul className="icon-list">
              {history.slice(0, 5).map((h) => (
                <li key={h.id}>
                  {formatDate(h.date)}: {h.value ?? "—"}%{h.comment ? `, ${h.comment}` : ""}
                  {h.approvalStatus && h.approvalStatus !== "NOT_REQUIRED" && (
                    <> <span className={`badge badge-${APPROVAL_STATUS[h.approvalStatus]?.tone || "neutral"}`}>{APPROVAL_STATUS[h.approvalStatus]?.label || h.approvalStatus}</span>{h.approvalStatus === "RETURNED" && h.approvalComment ? ` ${h.approvalComment}` : ""}</>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
